import axios, { type AxiosRequestConfig } from 'axios';
import * as Cookies from 'js-cookie';

import { WebOrders as WebOrdersService } from '~/api/orders-facade/generated/WebOrders';
import { LATEST_VERSION, V1, VERSION_HEADER } from '~/constants/api';
import { readFromLocalStorage } from '~/utils/local-storage';
import { addRequestListener } from '~/utils/request-watcher';

const setupBaseHeaders = (envId: string) => {
  const baseHeaders: Record<string, string> = {
    [VERSION_HEADER]: LATEST_VERSION,
  };

  if (envId === 'production') return baseHeaders;

  baseHeaders['X-HTTP-Request-Source'] = `React-${envId}`;
  const lang = readFromLocalStorage('i18nextLng');
  if (lang) baseHeaders['Accept-Language'] = lang;

  return baseHeaders;
};

const addShippoTokenAuth = (config: AxiosRequestConfig) => ({
  ...config,
  headers: {
    ...config.headers,
    Authorization: CONFIG.shippoToken
      ? `ShippoToken ${CONFIG.shippoToken}`
      : `JWT ${Cookies.get('jwt')}`,
  },
});

const addJWTTokenAuth = (config: AxiosRequestConfig) => ({
  ...config,
  headers: {
    ...config.headers,
    Authorization: `JWT ${Cookies.get('jwt')}`,
  },
});

const api = axios.create({
  baseURL: CONFIG.apiBaseUrl,
  headers: setupBaseHeaders(ENV_ID),
});

api.interceptors.request.use(addJWTTokenAuth);

export const addressAPI = axios.create({
  baseURL: CONFIG.addressUrl,
  headers: setupBaseHeaders(ENV_ID),
});
addressAPI.interceptors.request.use(addShippoTokenAuth);

export const ordersAPI = axios.create({
  baseURL: CONFIG.ordersUrl,
  headers: setupBaseHeaders(ENV_ID),
});
ordersAPI.interceptors.request.use(addShippoTokenAuth);

export const ordersV2API = axios.create({
  baseURL: CONFIG.ordersV2Url,
  headers: setupBaseHeaders(ENV_ID),
});
ordersV2API.interceptors.request.use(addShippoTokenAuth);

export const quickpackApi = axios.create({
  baseURL: CONFIG.quickpackUrl,
  headers: setupBaseHeaders(ENV_ID),
});
quickpackApi.interceptors.request.use(addShippoTokenAuth);

export const tagsAPI = axios.create({
  baseURL: CONFIG.ordersUrl,
  headers: setupBaseHeaders(ENV_ID),
});
tagsAPI.interceptors.request.use(addShippoTokenAuth);

export const armorAPI = axios.create({
  baseURL: CONFIG.armorUrl,
  headers: setupBaseHeaders(ENV_ID),
});
armorAPI.interceptors.request.use(addJWTTokenAuth);
addRequestListener({ instance: armorAPI, urlMatch: '/orders/batch_entry/refresh' });

export const unauthApi = axios.create({
  baseURL: CONFIG.apiBaseUrl,
  headers: setupBaseHeaders(ENV_ID),
});

export const djangoApi = axios.create({
  baseURL: CONFIG.djangoBaseUrl,
  headers: setupBaseHeaders(ENV_ID),
});
djangoApi.interceptors.request.use(addJWTTokenAuth);

export const storeConnectApi = axios.create({
  baseURL: CONFIG.storeConnectUrl,
  headers: setupBaseHeaders(ENV_ID),
});
storeConnectApi.interceptors.request.use(addJWTTokenAuth);

export const insightsApi = axios.create({
  baseURL: CONFIG.insightsUrl,
  headers: setupBaseHeaders(ENV_ID),
  timeout: 1000,
});
insightsApi.interceptors.request.use(addShippoTokenAuth);

export const analyticsApi = axios.create({
  baseURL: CONFIG.analyticsUrl,
  headers: setupBaseHeaders(ENV_ID),
});
analyticsApi.interceptors.request.use(addJWTTokenAuth);

export const apiV1 = axios.create({
  baseURL: CONFIG.apiBaseUrl,
  headers: {
    ...setupBaseHeaders(ENV_ID),
    [VERSION_HEADER]: V1,
  },
});
apiV1.interceptors.request.use(addJWTTokenAuth);

export const webOrdersApi = new WebOrdersService({
  baseURL: CONFIG.ordersUrl || CONFIG.apiBaseUrl,
  headers: setupBaseHeaders(ENV_ID),
});
webOrdersApi.instance.interceptors.request.use(addJWTTokenAuth);

export const paymentApi = axios.create({
  baseURL: CONFIG.paymentUrl,
  headers: setupBaseHeaders(ENV_ID),
});
paymentApi.interceptors.request.use(addJWTTokenAuth);

export default api;
